import React from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useTranslation } from "@emisgroup/acp-application-intl";
import { Environments } from "@emisgroup/cohort-builder-kit";
import { IRootState } from "../../../../../reducer/CombineReducer";
import { DeleteItem } from "../../Script";
import { BandingDetailsProps } from "../../Banding/Helper";
import { useUser } from "../../../../../AuthContext";
import { OutputConfigRemoveView } from "./RemoveOutputConfig.View";
import { ItemProps } from "./Helper";
import { AlertItem } from "./Types";
import { DeleteTypes } from "./Constants";

interface OutputConfigRemoveProps {
  /**
   * Contains output config item
   */
  item?: ItemProps;
  /**
   * Band id has to sent for band delete
   */
  bandItem?: BandingDetailsProps;
  /**
   * Type of delete
   */
  deleteType: string;
  /**
   * Config map id
   */
  mapId?: string;
  /**
   * Config map name
   */
  mapName?: string;
}

/**
 * Responsible for rendering the output config delete logic
 * @param props Contains output config id
 * @returns JSX element
 */
export const OutputConfigRemove = (props: OutputConfigRemoveProps): JSX.Element => {
  const { item, bandItem, deleteType, mapId, mapName } = props;
  const dispatch = useDispatch();
  const { t: translate } = useTranslation();
  const [
    extractState,
    outputConfig
  ] = useSelector((state: IRootState) => [
    state.extractState,
    state.outputConfig
  ], shallowEqual);
  const selectedExtract = extractState.selectedExtract;
  const extractGuid = selectedExtract?.extractGuid as string;
  const selectedConfigId = outputConfig.selectedOutputConfig.id;
  const { user } = useUser();
  const env = process.env.APP_TARGET_ENV as Environments;

  /**
   * Get alert content based on delete type
   * @returns Alert Item
   */
  const getAlertItem = (): AlertItem => {
    switch (deleteType) {
      case DeleteTypes.DELETE_OUTPUT_LIST:
        return {
          title: translate("OutputConfig.RemoveOutputConfigItem.Title", { type: "Output config" }),
          FirstMessage: translate("OutputConfig.RemoveOutputConfigItem.FirstMessage", { type: "Output config" }),
          Content: item && item.name || "",
          SecondMessage: translate("OutputConfig.RemoveOutputConfigItem.SecondMessage", { type: "Output config" })
        };
      case DeleteTypes.DELETE_BAND:
        return {
          title: translate("OutputConfig.RemoveOutputConfigItem.Title", { type: "Band" }),
          FirstMessage: translate("OutputConfig.RemoveOutputConfigItem.FirstMessage", { type: "Band" }),
          Content: bandItem && bandItem.name || "",
          SecondMessage: translate("OutputConfig.RemoveOutputConfigItem.SecondMessage")
        };
      case DeleteTypes.DELETE_OUTPUT_MAP_LIST:
        return {
          title: translate("OutputConfig.RemoveOutputConfigItem.Title", { type: "Output Config map" }),
          FirstMessage: translate("OutputConfig.RemoveOutputConfigItem.FirstMessage", { type: "Output Config map" }),
          Content: mapName || "",
          SecondMessage: translate("OutputConfig.RemoveOutputConfigItem.SecondMessage", { type: "Output Config map" })
        };
      default:
        return {} as AlertItem;
    }
  };

  return (
    <div>
      <OutputConfigRemoveView
        onDelete={(): Promise<void> => DeleteItem(
          dispatch,
          translate,
          item && item.outputConfigGuid || "",
          extractGuid,
          bandItem?.bandGuid || "",
          selectedConfigId,
          mapId || "",
          deleteType,
          env,
          user?.token
        )}
        alertItem={getAlertItem()}
        mapId={mapId}
      />
    </div>
  );
};
